import { useState } from 'react'
import dayjs, { type Dayjs } from 'dayjs'
import { Alert, Button, Card, DatePicker, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PaperClipOutlined, PlusOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { formatMoneyValue, money } from '@/shared/lib/money'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { CostCenterFilter } from '@/features/finance/CostCenterFilter'
import { ExpenseAttachmentsModal } from '@/features/finance/ExpenseAttachmentsModal'
import { ExpenseModal } from '@/features/finance/ExpenseModal'
import { journalMemo } from '@/features/finance/journalMemo'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import {
  fetchAccounts,
  fetchJournalEntries,
  fetchJournalTotals,
  fetchPeriods,
  type JournalEntry,
  type JournalEntryLine,
  type JournalFilterKey,
} from '@/features/finance/financeApi'

const ENTRY_KINDS = ['EXPENSE', 'PURCHASE', 'SUPPLIER_PAYMENT', 'PAYROLL', 'INVENTORY', 'STAKEHOLDER', 'PERIOD_CLOSE', 'REVERSAL'] as const

/**
 * Provodkalar jurnali va <b>xarajat kiritish</b>.
 *
 * <h2>⚠️ NEGA XARAJAT AYNAN SHU SAHIFADA</h2>
 * Xarajat kiritish provodka YOZADI, ya'ni uning natijasi shu jurnalda
 * ko'rinadi. Tugmani davrlar sahifasiga qo'yish operatorni "kiritdim,
 * qayerda?" holatiga tushirardi — kiritilgan xarajatni ko'rsatadigan
 * yagona ro'yxat shu yerda.
 *
 * <h2>⚠️ IKKI HUQUQ, IKKI XIL KO'RINISH</h2>
 * Jurnalni O'QISH `finance.period.view` ni talab qiladi, xarajat
 * kiritish esa `finance.expense.write` ni. Ular BOSHQA-BOSHQA odamlar
 * bo'lishi mumkin, shuning uchun sahifa ikkalasidan BIRI bo'lganda
 * ochiladi va jadval o'qish huquqi bo'lmaganda "ruxsat yo'q" deb ochiq
 * aytadi — bo'sh jadval ko'rsatib, uni "yozuv yo'q" deb o'qitmaydi.
 *
 * <h2>⚠️ YOZISH YO'LI IKKITA VA IKKALASI HAM ALOHIDA</h2>
 * Provodka faqat davr yopish va xarajat orqali paydo bo'ladi;
 * "ixtiyoriy provodka" endpointi backendda ATAYLAB ochilmagan
 * (`JournalController` javadoc'i). Shuning uchun bu sahifada tahrirlash
 * ham, o'chirish ham yo'q: `finance.journal_immutable()` ularni bazada
 * rad etadi.
 */
export function FinanceJournalPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()

  // ⚠️ N4: `created_by` ga server `auth.getName()` ni yozadi — ya'ni
  //    UUID (yoki login), NOM emas. Xarita bo'lmasa ustunda xom
  //    identifikator turardi. Sabab va yagona yechim `actorName.ts` da.
  const actors = useActorDirectory()

  const { state: table, patch } = useTableUrlState()
  const filterValue = (key: JournalFilterKey) => table.filters[key]?.[0] ?? null
  const setFilters = (next: Partial<Record<JournalFilterKey, string | null>>) => {
    const filters: Record<string, readonly string[]> = { ...table.filters }
    for (const [key, value] of Object.entries(next)) {
      if (value) filters[key] = [value]
      else delete filters[key]
    }
    patch({ page: 1, filters })
  }
  const from = filterValue('from')
  const to = filterValue('to')
  const range: [Dayjs, Dayjs] | null = from && to ? [dayjs(from), dayjs(to)] : null
  const [expenseOpen, setExpenseOpen] = useState(false)
  const [recorded, setRecorded] = useState<JournalEntry | null>(null)
  const [attachmentsFor, setAttachmentsFor] = useState<JournalEntry | null>(null)

  const canRead = can('finance.period.view')

  // ⚠️ Davrlar FAQAT o'qish huquqi bo'lganda so'raladi: usiz so'rov 403
  // qaytarardi va sahifa hech qanday foyda bermaydigan xato ko'rsatardi.
  const periods = useQuery({
    queryKey: queryKeys.finance.periods,
    queryFn: ({ signal }) => fetchPeriods(signal),
    enabled: canRead,
  })
  const accounts = useQuery({
    queryKey: queryKeys.finance.accounts(false),
    queryFn: ({ signal }) => fetchAccounts(false, signal),
    enabled: canRead,
  })
  const totals = useQuery({
    queryKey: queryKeys.finance.entryTotals(table.filters, table.search),
    queryFn: ({ signal }) => fetchJournalTotals(table, signal),
    enabled: canRead,
  })

  const lineColumns: ColumnsType<JournalEntryLine> = [
    {
      key: 'account',
      title: t('finance.entry.account'),
      render: (_, line) => (
        <Space size={6} wrap>
          <Typography.Text code>{line.accountCode}</Typography.Text>
          <Typography.Text>{seededName('accounts', line.accountCode, line.accountName, t)}</Typography.Text>
        </Space>
      ),
    },
    {
      key: 'costCenter',
      title: t('finance.entry.costCenter'),
      width: 220,
      // ⚠️ `null` = TAQSIMLANMAGAN (butun tashkilot), "noma'lum" EMAS.
      // Davr yopishning yig'ma provodkasi aynan shunday: ledger
      // agregatida filial o'lchovi YO'Q (§20.7 audit tuzatishi).
      render: (_, line) =>
        line.orgNodeId === null ? (
          <Tooltip title={t('finance.entry.unallocatedHint')}>
            <Typography.Text type="secondary">{t('finance.entry.unallocated')}</Typography.Text>
          </Tooltip>
        ) : (
          `${line.orgNodeName ?? ''} · ${line.orgNodeCode ?? ''}`
        ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.financeJournal')}
        description={t('finance.entry.intro')}
        extra={
          <Can permission="finance.expense.write">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setExpenseOpen(true)}>
              {t('finance.expense.title')}
            </Button>
          </Can>
        }
      />

      {/* ⚠️ Natija oyna YOPILGANDAN keyin ham ko'rinadi: operator nima
          yozilganini (qaysi davr, qancha) tasdiqlashi kerak */}
      {recorded ? (
        <Alert
          type="success"
          showIcon
          closable
          onClose={() => setRecorded(null)}
          style={{ marginBottom: 16 }}
          message={t('finance.expense.doneTitle')}
          description={t('finance.expense.doneBody', {
            period: recorded.periodCode,
            amount: formatMoneyValue(money(recorded.amount, recorded.currency)),
          })}
          action={
            <Button
              size="small"
              icon={<PaperClipOutlined />}
              onClick={() => setAttachmentsFor(recorded)}
            >
              {t('finance.attachment.attach')}
            </Button>
          }
        />
      ) : null}

      {periods.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(periods.error, t)}
        />
      ) : null}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('finance.entry.asOfTitle')}
        description={t('finance.entry.asOfBody')}
      />

      {canRead ? (
        <DataTable<JournalEntry>
          queryKey={queryKeys.finance.entries}
          fetchPage={fetchJournalEntries}
          searchable
          searchPlaceholder={t('finance.entry.searchMemo')}
          columns={[
            {
              key: 'entryDate',
              title: t('finance.entry.date'),
              width: 130,
              sorter: true,
              // Provodka sanasi — KALENDAR kuni (`LocalDate`), lahza emas:
              // vaqt zonasidan o'tkazilmaydi (`periodFormat.ts` dagi izoh).
              render: (_, row) => row.entryDate,
            },
            {
              key: 'period',
              title: t('finance.entry.period'),
              width: 110,
              render: (_, row) => row.periodCode,
            },
            {
              key: 'kind',
              title: t('finance.entry.kind'),
              width: 170,
              render: (_, row) => (
                <Tag color={row.kind === 'PERIOD_CLOSE' ? 'purple' : 'default'}>
                  {t(`entryKind.${row.kind}`, { defaultValue: row.kind })}
                </Tag>
              ),
            },
            {
              key: 'memo',
              title: t('finance.entry.memo'),
              render: (_, row) => journalMemo(row, t) ?? t('common.dash'),
            },
            {
              key: 'amount',
              title: t('finance.entry.amount'),
              width: 160,
              align: 'right',
              sorter: true,
              render: (_, row) => formatMoneyValue(money(row.amount, row.currency)),
            },
            {
              key: 'source',
              title: t('finance.entry.source'),
              width: 200,
              /*
                ⚠️ "QAYSI VAQT HOLATIGA" — QARORLAR.md §18, 3-band.
                Yig'ma provodka ledgerning MA'LUM bir lahzadagi holatidan
                olingan va u lahza jonli billing sahifasidagidan farq
                qiladi. Buni ko'rsatmaslik "ikki panelda ikki raqam"
                muammosini javobsiz qoldirardi.
              */
              render: (_, row) => (
                <Space direction="vertical" size={0}>
                  <span>{t(`entrySource.${row.source}`, { defaultValue: row.source })}</span>
                  {row.sourceAsOf === null ? null : (
                    <Tooltip title={utcTooltip(row.sourceAsOf)}>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {t('finance.entry.asOf', { at: formatDateTime(row.sourceAsOf) })}
                      </Typography.Text>
                    </Tooltip>
                  )}
                </Space>
              ),
            },
            {
              key: 'attachments',
              title: t('finance.attachment.column'),
              width: 130,
              render: (_, row) =>
                row.kind === 'EXPENSE' ? (
                  <Button
                    size="small"
                    icon={<PaperClipOutlined />}
                    onClick={() => setAttachmentsFor(row)}
                  >
                    {t('finance.attachment.open')}
                  </Button>
                ) : null,
            },
            {
              key: 'createdBy',
              title: t('finance.entry.createdBy'),
              width: 200,
              render: (_, row) => (
                <Space direction="vertical" size={0}>
                  <span>{actorLabel(row.createdBy, actors, t('common.dash'))}</span>
                  <Tooltip title={utcTooltip(row.createdAt)}>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {formatDateTime(row.createdAt)}
                    </Typography.Text>
                  </Tooltip>
                </Space>
              ),
            },
          ]}
          rowKey={(row) => row.id}
          scrollX={1390}
          summary={() => [
            {
              key: 'total',
              cells: {
                memo: t('finance.entry.totalFiltered', { count: totals.data?.entries ?? 0 }),
                amount: totals.data?.currency
                  ? formatMoneyValue(money(totals.data.amount, totals.data.currency))
                  : t('common.dash'),
              },
            },
          ]}
          toolbarExtra={
            <Space wrap>
              <Select<string>
                allowClear
                style={{ minWidth: 160 }}
                placeholder={t('finance.entry.allPeriods')}
                loading={periods.isPending}
                value={filterValue('periodId') ?? undefined}
                onChange={(value) => setFilters({ periodId: value ?? null })}
                options={(periods.data ?? []).map((period) => ({ value: period.id, label: period.code }))}
              />
              <DatePicker.RangePicker
                allowClear
                value={range}
                onChange={(value) =>
                  setFilters({
                    from: value?.[0]?.format('YYYY-MM-DD') ?? null,
                    to: value?.[1]?.format('YYYY-MM-DD') ?? null,
                  })
                }
              />
              <Select<string>
                allowClear
                style={{ minWidth: 170 }}
                placeholder={t('finance.entry.allKinds')}
                value={filterValue('kind') ?? undefined}
                onChange={(value) => setFilters({ kind: value ?? null })}
                options={ENTRY_KINDS.map((kind) => ({ value: kind, label: t(`entryKind.${kind}`) }))}
              />
              <Select<string>
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ minWidth: 220 }}
                placeholder={t('finance.entry.allAccounts')}
                loading={accounts.isPending}
                value={filterValue('accountId') ?? undefined}
                onChange={(value) => setFilters({ accountId: value ?? null })}
                options={(accounts.data ?? []).map((account) => ({
                  value: account.id,
                  label: `${account.code} · ${seededName('accounts', account.code, account.name, t)}`,
                }))}
              />
              <CostCenterFilter
                value={filterValue('orgNodeId')}
                onChange={(orgNodeId) => setFilters({ orgNodeId })}
              />
            </Space>
          }
          expandable={{
            expandedRowRender: (entry) => (
              <Table<JournalEntryLine>
                columns={[
                  ...lineColumns,
                  {
                    key: 'debit',
                    title: t('finance.entry.debit'),
                    width: 150,
                    align: 'right',
                    render: (_, line) =>
                      line.amount < 0
                        ? formatMoneyValue(money(-line.amount, entry.currency))
                        : null,
                  },
                  {
                    key: 'credit',
                    title: t('finance.entry.credit'),
                    width: 150,
                    align: 'right',
                    render: (_, line) =>
                      line.amount > 0 ? formatMoneyValue(money(line.amount, entry.currency)) : null,
                  },
                ]}
                dataSource={[...entry.lines]}
                rowKey={(line) => line.id}
                size="small"
                pagination={false}
              />
            ),
          }}
          emptyTitle={t('finance.entry.empty')}
          emptyDescription={t('finance.entry.emptyHint')}
        />
      ) : (
        <Card>
          {/* ⚠️ Bo'sh jadval EMAS: "yozuv yo'q" va "ko'rish huquqim yo'q"
              ikki BOSHQA javob va ularni bir xil ko'rsatish yolg'on
              bo'lardi */}
          <EmptyState
            variant="no-permission"
            title={t('finance.entry.noReadTitle')}
            description={t('finance.entry.noReadBody')}
          />
        </Card>
      )}

      <ExpenseModal
        open={expenseOpen}
        periods={canRead && periods.isSuccess ? (periods.data ?? []) : null}
        onClose={() => setExpenseOpen(false)}
        onDone={setRecorded}
      />

      <ExpenseAttachmentsModal entry={attachmentsFor} onClose={() => setAttachmentsFor(null)} />
    </>
  )
}
